import { getApiBaseForModule } from '../../config/apiConfig';
import { App as CapacitorApp } from '@capacitor/app';
import { isAndroidNative } from '../../mobile/capacitorPlatform';
import { createAndroidUpdateCheck } from '../../services/androidUpdateCheck';

const updateCheck = createAndroidUpdateCheck({ load: fetchLatestAndroidApkInfo });

function buildMobileApiUrl(path) {
    const baseUrl = String(getApiBaseForModule('mobileApp') || '/api').replace(/\/$/, '');
    return `${baseUrl}/${String(path || '').replace(/^\//, '')}`;
}

async function tryFetchJson(url, token) {
    const headers = {
        Accept: 'application/json',
    };

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
        const response = await fetch(url, { method: 'GET', headers, signal: controller.signal });
        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}`);
            error.status = response.status;
            throw error;
        }
        const contentType = String(response.headers.get('content-type') || '').toLowerCase();
        if (!contentType.includes('application/json')) {
            const error = new Error('INVALID_JSON_RESPONSE');
            error.status = response.status;
            throw error;
        }
        return await response.json();
    } finally { clearTimeout(timer); }
}

function buildLatestUrl(basePath, currentVersion) {
    if (!currentVersion) {
        return basePath;
    }

    const params = new URLSearchParams({
        current_version: String(currentVersion),
    });

    return `${basePath}?${params.toString()}`;
}

async function fetchLatestAndroidApkInfo(options = {}) {
    const currentVersion = options?.currentVersion || null;
    const token = options?.token || null;
    const apiUrl = buildLatestUrl(buildMobileApiUrl('/mobile-app/android/latest'), currentVersion);
    const webUrl = buildLatestUrl('/mobile-app/android/latest', currentVersion);
    const candidates = [...new Set([apiUrl, webUrl])];
    let lastError = null;

    for (const url of candidates) {
        try {
            return await tryFetchJson(url, token);
        } catch (error) {
            lastError = error;
            if ([401, 403].includes(error.status)) throw error;
        }
    }

    throw lastError || new Error('APK_INFO_UNAVAILABLE');
}

export async function getLatestAndroidApkInfo(options = {}) {
    let currentVersion = options.currentVersion || null;
    if (!currentVersion && isAndroidNative()) {
        try { currentVersion = (await CapacitorApp.getInfo()).version || null; } catch { /* Se consulta sin versión si el plugin no responde. */ }
    }
    return updateCheck.check({ ...options, currentVersion });
}

export async function downloadLatestAndroidApk(token, options = {}) {
    const onProgress = typeof options?.onProgress === 'function' ? options.onProgress : null;
    const latest = await getLatestAndroidApkInfo({ token });

    const directFileUrl = typeof latest?.directFileUrl === 'string' && latest.directFileUrl.trim() !== ''
        ? latest.directFileUrl.trim()
        : null;

    const headers = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    let response;
    let usedDirectFileUrl = false;

    try {
        response = await fetch(buildMobileApiUrl('/mobile-app/android/latest/download'), {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}`);
            error.status = response.status;
            throw error;
        }
    } catch (error) {
        if (!directFileUrl) {
            throw error;
        }

        response = await fetch(directFileUrl, {
            method: 'GET',
            headers: {
                Accept: 'application/vnd.android.package-archive,application/octet-stream,*/*',
            },
        });
        usedDirectFileUrl = true;

        if (!response.ok) {
            const fallbackError = new Error(`HTTP ${response.status}`);
            fallbackError.status = response.status;
            throw fallbackError;
        }
    }

    const contentDisposition = response.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
    let filename = 'app-android.apk';

    if (filenameMatch?.[1]) {
        filename = decodeURIComponent(filenameMatch[1].replace(/\"/g, '').trim());
    } else if (usedDirectFileUrl && directFileUrl) {
        try {
            const pathname = new URL(directFileUrl, window.location.origin).pathname;
            const candidate = pathname.split('/').pop();
            if (candidate) {
                filename = decodeURIComponent(candidate);
            }
        } catch {
            // Mantener fallback por defecto.
        }
    } else if (typeof latest?.filename === 'string' && latest.filename.trim() !== '') {
        filename = latest.filename.trim();
    }

    let blob;
    const contentLengthHeader = response.headers.get('content-length');
    const totalBytes = Number(contentLengthHeader || 0);

    if (response.body && typeof response.body.getReader === 'function') {
        const reader = response.body.getReader();
        const chunks = [];
        let receivedBytes = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }

            if (value) {
                chunks.push(value);
                receivedBytes += value.length;

                if (onProgress) {
                    const ratio = totalBytes > 0
                        ? Math.min(1, receivedBytes / totalBytes)
                        : null;

                    onProgress({
                        receivedBytes,
                        totalBytes,
                        progress: ratio,
                    });
                }
            }
        }

        blob = new Blob(chunks, {
            type: response.headers.get('content-type') || 'application/vnd.android.package-archive',
        });
    } else {
        blob = await response.blob();
        if (onProgress) {
            onProgress({
                receivedBytes: blob.size,
                totalBytes: blob.size,
                progress: 1,
            });
        }
    }

    return {
        blob,
        filename,
    };
}
